import React from 'react';
import { useDateFormat, useTimeFormat } from '../../context/LocaleContext';
import PopoverToolbar from './popover/PopoverToolbar';
import AttendeeRow from './popover/AttendeeRow';
import useAnchoredPanel from '../../hooks/useAnchoredPanel';
import { coreSync } from '../../lib/core';
import s from './popover/popover.module.css';

// The event popover (SPEC.md §2.2).
//
// Clicking a lesson opens this, not the attendance drawer: most clicks on a
// calendar are to look — when is it, who is it, what does it cost — and only
// some are to record attendance.

export default function EventPopover({
  occurrence, anchor, students, locations = [], priceFor,
  onClose, onEdit, onDelete, onTakeAttendance, statusFor,
  // The abbreviation for the zone this lesson's series was created in, only
  // when that is not the studio's current one (screenZoneLabel in
  // domain/timeZone.js). Resolved by CalendarPage, because the zone belongs to
  // the series and this component is handed an occurrence.
  zoneLabel = '',
}) {
  const formatDate = useDateFormat();
  const formatTime = useTimeFormat();
  // Anchored to the lesson that was clicked, which goes in as `anchor` so the
  // first placement already has it (#580).
  const { panelRef, style } = useAnchoredPanel({
    open: Boolean(occurrence), align: 'left', gap: 6, anchor: anchor || null,
  });

  if (!occurrence) return null;

  const attendees = occurrence.studentIds;
  // A group event has no single attendance state, so only a one-student lesson
  // reports one.
  const soleStatus = attendees.length === 1
    ? coreSync().resolveAttendanceStatus(statusFor(occurrence.id, attendees[0])).label
    : null;

  return (
    <>
      <div className={s.scrim} onClick={onClose} />

      <div
        className={s.panel}
        ref={panelRef}
        style={style || { position: 'fixed', visibility: 'hidden' }}
      >
        <PopoverToolbar onEdit={onEdit} onDelete={onDelete} onClose={onClose} />

        <div className={s.title}>{occurrence.categoryLabel}</div>
        {/* The studio's own date and clock, not the stored strings. */}
        <div className={s.line}>{formatDate(occurrence.date)}</div>
        <div className={occurrence.locationId ? s.line : s.lineLast}>
          {/* "All Day" in place of the clock and the duration (#160), and no
              zone label: there is no four o'clock to disambiguate. */}
          {occurrence.allDay
            ? 'All Day'
            : <>{formatTime(occurrence.startTime)}{zoneLabel ? ` ${zoneLabel}` : ''} · {occurrence.durationMin} min</>}
        </div>
        {occurrence.locationId ? (
          <div className={s.lineLast}>
            {locations.find((l) => l.id === occurrence.locationId)?.label || 'Unknown location'}
          </div>
        ) : null}

        <div className={s.sectionLabel}>
          {attendees.length} Attendee{attendees.length === 1 ? '' : 's'}
        </div>

        {attendees.map((sid) => {
          const student = students.find((x) => x.id === sid);
          // Gated as the Day view's "billed today" tile and the forecast fold
          // are (#159): `isBillableFor` reading the one table in
          // core/src/attendance.rs, so a Teacher Cancelled lesson shows no
          // figure (#158). With the event's `This event requires a make-up
          // credit` (#362), an unmarked attendee on a flagged lesson shows no
          // figure either.
          const priceMinor = coreSync()
            .isBillableFor(statusFor(occurrence.id, sid), occurrence.requiresMakeupCredit === true)
            ? priceFor(occurrence, student)
            : null;
          return (
            <AttendeeRow
              key={sid}
              student={student}
              priceMinor={priceMinor}
            />
          );
        })}

        {soleStatus ? <div className={s.status}>{soleStatus}</div> : null}

        <button className={s.primary} onClick={onTakeAttendance}>Take Attendance</button>
      </div>
    </>
  );
}
